<template>
  <div class="comparative-analysis-table">
    <s-table
      responsive
      striped
      fixed
      show-empty
      :fields="fields"
      :items="items"
      :busy="loading"
      :empty-text="$t('general.empty-list')"
    >
      <template v-slot:cell()="data">
        <div
          class="comparative-analysis-table__cell-container"
          :class="{
            'comparative-analysis-table__cell-container_alarmed': getIsAlarmed(data),
          }"
        >
          <template v-if="hasDiagnostics(data)">
            <div
              v-for="(diagnostic, index) in getDiagnostics(data)"
              :key="`${index}-${data.index}`"
              class="comparative-analysis-table__cell-value"
            >
              <classification
                :classification="diagnostic.classification"
                show-label
                show-tooltip
              />
              <span
                class="comparative-analysis-table__diagnostic-text"
                :class="{ 'truncate': showBadgeData(diagnostic.description) }"
              >
                {{ diagnostic.description }}
              </span>
              <info-badge
                class="comparative-analysis-table__info-badge"
                v-if="showBadgeData(diagnostic.description)"
                :string-key="diagnostic.description"
              />
            </div>
          </template>
          <span v-else>-</span>
        </div>
      </template>
      <template v-slot:cell(sample)="data">
        <span
          class="text-nowrap general-table__field"
          :class="{
            'comparative-analysis-table__cell_alarmed font-weight-bold': data.item.alarmed
          }"
        >
          {{ $n(data.item.sampleNumber) }}
          <div
            v-if="data.item.alarmed"
            class="ml-2"
          >
            <i
              aria-hidden="true"
              class="fas fa-exclamation-triangle"
            />
          </div>
        </span>
      </template>
      <template
        v-slot:thead-top
      >
        <tr class="comparative-analysis-table__thead-top-row">
          <th id="sample"><span class="sr-only">
            {{ $t('fields.engineering.chromatography.sample') }}
          </span></th>
          <th id="nbr-7274">
            {{ $t('enums.engineering.chromatography.rules.nbr-7274') }}
          </th>
          <th
            id="iec-60599"
            colspan="1"
          >
            {{ $t('enums.engineering.chromatography.rules.iec-60599') }}
          </th>
          <th
            id="duval"
            colspan="3"
          >
            {{ $t('enums.engineering.chromatography.rules.duval') }}
          </th>
          <th
            id="iee-c57"
            colspan="2"
          >
            {{ $t('enums.engineering.chromatography.rules.ieee-c57-104') }}
          </th>
        </tr>
      </template>
    </s-table>
    <grid-paginator
      @input="getComparativeAnalysisTableData"
      v-model="pagination.currentPage"
      :per-page="pagination.pageSize"
      :total-rows="pagination.totalRows"
      class="comparative-analysis-table__paginator pt-3"
    />
  </div>
</template>

<script>
import comparativeAnalysisService from '@/domain/services/installation/engineering/chromatography/comparativeAnalysis';
import themeService from '@/services/themeService';
import GridPaginator from '@/components/lists/Paginator';
import InfoBadge from '@/components/icon/InfoBadge';
import LoadingMixin from '@/services/loadingMixin';
import comparativeAnalysisTableFields from '@/domain/data/installation/engineering/chromatography/methodsAndDiagnostics/comparativeAnalysis/comparativeAnalysisTableFields';
import { getUniqueId } from '@/services/createId';
import Classification from '@/components/Classification';

export default {
  name: 'ComparativeAnalysisTable',
  components: {
    GridPaginator,
    InfoBadge,
    Classification,
  },
  props: {
    sampleIds: Array,
    assetModuleId: String,
  },
  mixins: [
    LoadingMixin,
  ],
  data: () => ({
    items: [],
    pagination: {
      pageSize: 10,
      totalRows: null,
      currentPage: 1,
    },
  }),
  computed: {
    fields() {
      return comparativeAnalysisTableFields.map(field => ({
        ...field,
        label: this.$t(field.label),
      }));
    },
    isScreenSizeXsOrSm() {
      return themeService.isScreenSize('xs') || themeService.isScreenSize('md');
    },
  },
  methods: {
    getUniqueId,
    showBadgeData(data) {
      if (this.isScreenSizeXsOrSm && !this.isTextBiggerThan25(data)) {
        return true;
      }
      return this.isTextBiggerThan25(data);
    },
    getItem(data) {
      return data.item[data.field.key] || {};
    },
    getDiagnostics(data) {
      return this.getItem(data).diagnostics || [];
    },
    getIsAlarmed(data) {
      return this.getItem(data).alarmed;
    },
    hasDiagnostics(data) {
      return !!this.getDiagnostics(data).length;
    },
    isTextBiggerThan25(data) {
      return data.length > 25;
    },
    getComparativeAnalysisTableData() {
      this.startLoading();
      comparativeAnalysisService.getComparativeAnalysis(this.assetModuleId, {
        page: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        filter: {
          samplesIds: this.sampleIds,
        },
      })
      .then((response) => {
        this.pagination.page = response.page;
        this.pagination.pageSize = response.pageSize;
        this.pagination.totalRows = response.total;
        this.items = comparativeAnalysisService.transformResponseToTableArray(response.data);
      })
      .finally(() => { this.endLoading(); });
    },
  },
  created() {
    this.getComparativeAnalysisTableData();
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .comparative-analysis-table
    &__thead-top-row
      color: var(--color-primary)
    &__cell-value
      margin: 5px 2px
      display: flex
      justify-content: center
      align-items: center
    &__diagnostic-text
      margin-right: 10px
    &__info-badge
      position: relative
      top: 2px
    &__cell-container
      display: flex
      justify-content: space-between
      flex-direction: column
    &__cell
      &_alarmed
        display: flex
        align-items: center
        justify-content: center
        color: var(--color-danger)
    ::v-deep
      table
        display: flex
        flex-direction: column
        overflow: auto
        max-height: 450px
        min-width: 1000px
      tr
        display: table
        width: 100%
        table-layout: fixed
      td:last-child
        border-right: none
      td:first-child, th:first-child
        width: 90px
        border-left: none
      th, td
        text-align: center !important
      tr th, tr td
        white-space: normal !important
        border-top: 2px solid var(--color-background-light)
        border-left: 2px solid var(--color-background-light)
        border-right: 2px solid var(--color-background-light)
      thead
        position: sticky
        top: 0
        z-index: 1
        border-left: 1px solid var(--color-background-light)
        border-right: 1px solid var(--color-background-light)
        border-bottom: 1px solid $color-border-gray
        tr:last-child th
          border-bottom: 1px solid var(--color-background-light)
        tr:first-child th
          top: 0 !important
        th:nth-child(2)
          border-left: none
        th:first-child
          border-left: none
          border-right: none
          background-color: var(--color-background-light) !important
        th:last-child
          border-right: none
      tbody
        border-left: 1px solid $color-border-gray
        border-right: 1px solid $color-border-gray
        border-bottom: 1px solid $color-border-gray
</style>
